<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue';
import { useGeolocationStore } from '@/modules/geolocation';
import { useAlertStore } from '@/modules/alerts';
import { useSettingsStore } from '@/modules/settings';
import { useDetectionStore } from '@/modules/detection';
import {
  getDeviceId,
  isPushSupported,
  isPushEnabled,
  enablePush,
  disablePush,
} from '@/modules/platform';
import RadarMiniChart from './RadarMiniChart.vue';
import type { LiveDetection } from './useLiveDetection';
import type { EventClassName } from '@/types';
import { EXPLOSION_CLASS_ICONS } from '@/types/geolocation';

/**
 * Шапка радара: статус-заголовок, чип мониторинга и ряд бейджей
 * (соединение, тревога, мониторящие рядом, id сенсора, точность, синк,
 * push, карта, настройки). Доменные store'ы читает сам; действия,
 * принадлежащие странице (мониторинг/синк/тогглы панелей), — через emits.
 */

const props = defineProps<{
  detection: LiveDetection;
  locating: boolean;
  waitingForAlert: boolean;
  accuracy: number | null;
  locationLabel: string | null;
  syncing: boolean;
  showMap: boolean;
  showQuadrants: boolean;
  showTools: boolean;
}>();

const emit = defineEmits<{
  'toggle-monitoring': [];
  sync: [];
  'toggle-map': [];
  'toggle-tools': [];
  'toggle-quadrants': [];
  error: [msg: string];
  'restart-onboarding': [];
}>();

const store = useGeolocationStore();
const alertStore = useAlertStore();
const settings = useSettingsStore();
const detectionStore = useDetectionStore();

// ── Last detection chip (icon + confidence next to the mic button) ───────────
// The percentage shown by the mic button is the confidence of the last *fired* detection.
// It is not a live level — so we let it auto-clear 5 minutes after the last detection, and
// pair it with a per-class icon so the user sees *what* was caught, not just a number.
const DETECTION_TTL_MS = 5 * 60 * 1000;

/** Per-class glyphs for the mic chip — shared with the own-events feed. */
const CLASS_ICONS = EXPLOSION_CLASS_ICONS;

const lastDetConf = ref<number | null>(null);
const lastDetClass = ref<EventClassName | null>(null);
let detTimer: ReturnType<typeof setTimeout> | null = null;

function clearDetection(): void {
  lastDetConf.value = null;
  lastDetClass.value = null;
  if (detTimer) {
    clearTimeout(detTimer);
    detTimer = null;
  }
}

// Show the latest detection and (re)arm the 5-minute auto-reset. Ignores stale events restored
// from IndexedDB on load (an hours-old event must not light the chip), and trims the timer to the
// event's remaining lifetime so a freshly-loaded recent event still clears on schedule.
watch(
  () => detectionStore.lastEvent?.id ?? detectionStore.lastEvent?.createdAt ?? null,
  (key) => {
    if (key === null) return;
    const ev = detectionStore.lastEvent;
    if (!ev) return;
    const age = Date.now() - ev.createdAt;
    if (age >= DETECTION_TTL_MS) return;
    lastDetConf.value = ev.confidence;
    lastDetClass.value = ev.eventClass;
    if (detTimer) clearTimeout(detTimer);
    detTimer = setTimeout(clearDetection, DETECTION_TTL_MS - age);
  },
);

onUnmounted(() => {
  if (detTimer) clearTimeout(detTimer);
});

// ── Headline ────────────────────────────────────────────────────────────────

const statusHeadline = computed(() => {
  if (props.locating) return 'Определяем местоположение…';
  const n = store.nearbyCount;
  if (n === 0) return 'Спокойно';
  return `${n} ${pluralEvents(n)} рядом`;
});

const statusTone = computed(() => (store.nearbyCount > 0 ? 'alert' : 'calm'));

function pluralEvents(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'событие';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'события';
  return 'событий';
}

const monitorLabel = computed(() => {
  if (props.detection.initializing.value) return 'Загрузка модели…';
  if (props.waitingForAlert) return 'Ждёт тревоги — детектор включится автоматически';
  if (props.detection.monitoring.value) {
    return props.detection.status.value === 'detected' ? 'Обнаружено!' : 'Слушаю…';
  }
  return 'Включить мониторинг';
});

/** Header badge: air-raid alert status around the user (hidden in the normal power mode). */
const alertBadge = computed<{ icon: string; text: string; cls: string } | null>(() => {
  if (settings.powerMode === 'normal') return null;
  const active = alertStore.alertActive;
  if (active === true) {
    const name = alertStore.displayOblast?.replace(' область', ' обл.');
    return { icon: '🟥', text: name ? `Тревога · ${name}` : 'Тревога', cls: 'alert-on' };
  }
  if (active === false) return { icon: '🟢', text: 'Нет тревоги', cls: 'alert-off' };
  return { icon: '⚪', text: 'Тревога: нет данных', cls: 'alert-unknown' };
});

// ── Device id badge ─────────────────────────────────────────────────────────

/** This sensor's stable (hashed) id, shown to the user so they can identify their device. */
const myDeviceId = ref('');
const idCopied = ref(false);
getDeviceId().then((id) => {
  myDeviceId.value = id;
});

/** Copy the device id to the clipboard with a short "copied" confirmation. */
async function copyDeviceId(): Promise<void> {
  try {
    await navigator.clipboard.writeText(myDeviceId.value);
    idCopied.value = true;
    setTimeout(() => (idCopied.value = false), 1500);
  } catch {
    /* clipboard unavailable — ignore */
  }
}

// ── Background push notifications (standalone PWA only) ─────────────────────

const pushSupported = isPushSupported();
const pushOn = ref(isPushEnabled());
const pushBusy = ref(false);

async function togglePush(): Promise<void> {
  if (pushBusy.value) return;
  pushBusy.value = true;
  try {
    if (pushOn.value) {
      await disablePush();
      pushOn.value = false;
    } else {
      const center = store.userCenter;
      if (!center) {
        emit('error', 'Сначала определите местоположение');
        return;
      }
      await enablePush(center);
      pushOn.value = true;
    }
  } catch (err) {
    emit('error', err instanceof Error ? err.message : 'Не удалось включить уведомления');
  } finally {
    pushBusy.value = false;
  }
}
</script>

<template>
  <header
    class="summary"
    :class="statusTone"
  >
    <div class="summary-top">
      <div class="headline">
        {{ statusHeadline }}
      </div>
      <!-- Mic control + a tiny 1-hour detection-activity sparkline beside it -->
      <div class="mic-group">
        <RadarMiniChart :detection="detection" />
        <!-- Monitoring control as a compact chip in the top-right corner -->
        <button
          class="mic-chip"
          data-onboarding-target="mic-chip"
          :class="{
            active: detection.monitoring.value,
            loading: detection.initializing.value,
            detected: detection.status.value === 'detected',
            waiting: waitingForAlert,
          }"
          :disabled="detection.initializing.value"
          :title="monitorLabel"
          @click="emit('toggle-monitoring')"
        >
          <span class="mic-icon">{{ detection.monitoring.value ? '⏹' : waitingForAlert ? '🌙' : '🎤' }}</span>
          <span
            v-if="detection.monitoring.value && lastDetConf !== null"
            class="mic-conf"
            :title="`Последнее срабатывание · сбросится через 5 мин без событий`"
          >
            <span
              v-if="lastDetClass"
              class="mic-cls"
            >{{ CLASS_ICONS[lastDetClass] }}</span>
            {{ (lastDetConf * 100).toFixed(0) }}%
          </span>
          <span
            v-else
            class="mic-text"
          >{{ detection.initializing.value ? '…' : detection.monitoring.value ? '' : waitingForAlert ? 'Ждёт тревоги' : 'Слушать' }}</span>
        </button>
      </div>
    </div>
    <div class="badges">
      <span
        class="badge conn"
        :class="store.connected ? 'online' : 'offline'"
        :title="store.connected ? 'онлайн' : 'офлайн'"
      >
        <span class="conn-dot">{{ store.connected ? '●' : '○' }}</span>
        <span class="conn-text">{{ store.connected ? 'онлайн' : 'офлайн' }}</span>
      </span>
      <!-- <span class="badge radius">20 км</span> -->
      <span
        v-if="alertBadge"
        class="badge alert-badge"
        :class="alertBadge.cls"
        :title="alertStore.cachedat ? `Тревоги: данные на ${alertStore.cachedat}` : 'Статус воздушной тревоги рядом с вами'"
      >
        {{ alertBadge.icon }} <span class="alert-text">{{ alertBadge.text }}</span>
      </span>
      <button
        v-if="detection.monitoring.value"
        class="badge monitors"
        :class="{ alone: store.monitorsNearby === 0, active: showQuadrants }"
        title="Нажмите, чтобы показать/скрыть зоны онлайн-мониторов на карте"
        @click="emit('toggle-quadrants')"
      >
        👥
        <span class="monitors-num">{{ store.monitorsNearby }}</span>
        <span class="monitors-text">{{ store.monitorsNearby > 0 ? `${store.monitorsNearby} рядом` : 'вы один' }}</span>
      </button>
      <button
        v-if="myDeviceId"
        class="badge id-badge"
        :title="`Ваш ID сенсора: ${myDeviceId} (нажмите, чтобы скопировать)`"
        @click="copyDeviceId"
      >
        {{ idCopied ? '✅' : '🆔' }} <span class="id-text">{{ idCopied ? 'скопировано' : myDeviceId }}</span>
      </button>
      <span
        v-if="accuracy !== null"
        class="badge acc desktop-only"
        :title="locationLabel ? `Источник: ${locationLabel}` : ''"
      >
        📍 ±{{ Math.round(accuracy) }} м<template v-if="locationLabel"> · {{ locationLabel }}</template>
      </span>
      <!-- <button
        class="badge sync"
        :disabled="syncing"
        :title="syncing ? 'обновление…' : 'обновить'"
        @click="emit('sync')"
      >
        <span class="sync-icon">⟳</span>
        <span class="sync-text">{{ syncing ? '…' : 'обновить' }}</span>
      </button> -->
      <button
        v-if="pushSupported"
        class="badge push-btn"
        :class="{ on: pushOn }"
        :disabled="pushBusy"
        :title="pushOn ? 'уведомления рядом включены — нажмите, чтобы выключить' : 'уведомлять о событиях рядом, даже когда приложение закрыто'"
        @click="togglePush"
      >
        <span class="push-icon">{{ pushOn ? '🔔' : '🔕' }}</span>
        <span class="push-text">{{ pushBusy ? '…' : pushOn ? 'Уведомления' : 'Уведомить' }}</span>
      </button>
      <button
        class="badge map-btn"
        :class="{ off: !showMap }"
        :title="showMap ? 'скрыть карту' : 'показать карту'"
        @click="emit('toggle-map')"
      >
        <span class="map-icon">🗺</span>
        <span class="map-text">{{ showMap ? 'Карта' : 'Карта скрыта' }}</span>
      </button>
      <button
        class="badge tools-btn"
        data-onboarding-target="tools-btn"
        :class="{ open: showTools }"
        :title="showTools ? 'свернуть настройки' : 'настройки и диагностика'"
        @click="emit('toggle-tools')"
      >
        <span class="tools-icon">⚙</span>
        <span class="tools-text">{{ showTools ? 'Скрыть' : 'Настройки' }}</span>
      </button>
      <button
        class="badge help-btn"
        title="Показать подсказки заново"
        @click="emit('restart-onboarding')"
      >
        <span class="help-icon">❓</span>
      </button>
    </div>
    <div
      v-if="store.isStaleData"
      class="stale"
    >
      ⚠ данные могли устареть ({{ store.staleDuration }})
    </div>
  </header>
</template>

<style scoped>
/* ── Header ── */
.summary {
  padding: 16px 18px 12px;
  flex-shrink: 0;
  transition: background 0.4s;
  position: relative;
  z-index: 1;
}
.summary.calm {
  background: linear-gradient(135deg, #16321f 0%, #14253a 100%);
}
.summary.alert {
  background: linear-gradient(135deg, #3a1414 0%, #2a1430 100%);
}
.summary-top {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
}
.headline {
  font-size: 22px;
  font-weight: 700;
  letter-spacing: 0.2px;
  min-width: 0;
}

/* Mic control grouped with its activity sparkline, pinned to the top-right. */
.mic-group {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

/* ── Monitoring chip (top-right of header) ── */
.mic-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 8px 12px;
  border: none;
  border-radius: 20px;
  font-size: 14px;
  font-weight: 700;
  color: #fff;
  cursor: pointer;
  background: linear-gradient(135deg, #2aabee 0%, #1f7a44 100%);
  box-shadow: 0 2px 10px rgba(42, 171, 238, 0.3);
}
.mic-chip.active {
  background: linear-gradient(135deg, #1f7a44 0%, #14253a 100%);
}
.mic-chip.active .mic-icon {
  animation: pulse 1.4s ease-in-out infinite;
}
.mic-chip.detected {
  background: linear-gradient(135deg, #c0392b 0%, #7a2a2a 100%);
  animation: flash 0.6s ease-out;
}
.mic-chip.loading {
  opacity: 0.7;
  cursor: default;
}
/* Armed but dormant (alertOnly, no alert nearby): muted look, wakes up on its own. */
.mic-chip.waiting {
  background: linear-gradient(135deg, #3a4660 0%, #232c40 100%);
  box-shadow: none;
  color: #aeb8cc;
}
.mic-chip:disabled {
  cursor: default;
}
.mic-chip .mic-icon {
  font-size: 16px;
  line-height: 1;
}
.mic-chip .mic-conf {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-variant-numeric: tabular-nums;
}
.mic-chip .mic-cls {
  font-size: 14px;
  line-height: 1;
}
.mic-chip .mic-text {
  font-size: 13px;
}
@keyframes pulse {
  0%,
  100% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.25);
    opacity: 0.7;
  }
}
@keyframes flash {
  from {
    box-shadow: 0 0 0 0 rgba(231, 76, 60, 0.7);
  }
  to {
    box-shadow: 0 0 0 16px rgba(231, 76, 60, 0);
  }
}

/* ── Badges ── */
.badges {
  display: flex;
  gap: 8px;
  margin-top: 10px;
  align-items: center;
  flex-wrap: wrap;
}
.badge {
  font-size: 12px;
  padding: 4px 10px;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.12);
  color: #dfe5ee;
  border: none;
}
.badge.conn {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.badge.conn.online {
  background: #1f7a44;
  color: #fff;
}
.badge.conn.offline {
  background: #7a2a2a;
  color: #fff;
}
/* Air-raid alert status around the user (shown in adaptive / alertOnly power modes). */
.badge.alert-badge {
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.badge.alert-badge.alert-on {
  background: #7a2a2a;
  color: #fff;
}
.badge.alert-badge.alert-off {
  background: #1f7a44;
  color: #fff;
}
.badge.alert-badge.alert-unknown {
  background: rgba(255, 255, 255, 0.12);
  color: #aeb8cc;
}
/* On phones show only the status emoji; the text label appears on wider (desktop) screens. */
.alert-text {
  display: none;
}
@media (min-width: 768px) {
  .alert-text {
    display: inline;
  }
}
.conn-dot {
  line-height: 1;
}
/* On phones show only the status dot; the text label appears on wider (desktop) screens. */
.conn-text {
  display: none;
}
@media (min-width: 768px) {
  .conn-text {
    display: inline;
  }
}
.badge.acc {
  background: rgba(42, 171, 238, 0.18);
  color: #9bd6f7;
  font-variant-numeric: tabular-nums;
}
.badge.desktop-only {
  display: none;
}
@media (min-width: 768px) {
  .badge.desktop-only {
    display: inline-flex;
  }
}
.badge.monitors {
  cursor: pointer;
  background: rgba(46, 204, 113, 0.22);
  color: #b8f0d0;
  font-variant-numeric: tabular-nums;
}
.badge.monitors.alone {
  background: rgba(255, 255, 255, 0.12);
  color: #aeb8cc;
}
.badge.monitors.active {
  background: rgba(255, 215, 0, 0.35);
  color: #ffe066;
  box-shadow: 0 0 6px rgba(255, 215, 0, 0.4);
}
/* On phones show only the count digit next to 👥; the full label appears on wider (desktop) screens. */
.monitors-text {
  display: none;
}
.monitors-num {
  display: inline;
}
@media (min-width: 768px) {
  .monitors-text {
    display: inline;
  }
  .monitors-num {
    display: none;
  }
}
.badge.id-badge {
  cursor: pointer;
  max-width: 160px;
  display: inline-flex;
  align-items: center;
  gap: 4px;
}
.id-badge .id-text {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-variant-numeric: tabular-nums;
  font-family: ui-monospace, SFMono-Regular, Menlo, monospace;
  font-size: 11px;
  /* On phones show only the 🆔 icon; the id text appears on wider (desktop) screens. */
  display: none;
}
@media (min-width: 768px) {
  .id-badge .id-text {
    display: inline;
  }
}
.badge.sync,
.badge.map-btn,
.badge.tools-btn,
.badge.push-btn,
.badge.help-btn {
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
}
.badge.sync:disabled,
.badge.push-btn:disabled {
  opacity: 0.6;
  cursor: default;
}
.badge.push-btn.on {
  background: rgba(46, 204, 113, 0.22);
  color: #b8f0d0;
}
.badge.tools-btn.open {
  background: rgba(42, 171, 238, 0.22);
  color: #cde9fb;
}
.badge.map-btn.off {
  opacity: 0.55;
}
/* On phones these controls collapse to their icon; the label appears on wider (desktop) screens. */
.sync-text,
.map-text,
.tools-text,
.push-text {
  display: none;
}
@media (min-width: 768px) {
  .sync-text,
  .map-text,
  .tools-text,
  .push-text {
    display: inline;
  }
}
.stale {
  margin-top: 8px;
  font-size: 12px;
  color: #f1c40f;
}
</style>
